import { config } from './config.js';

// Phase 2B drag-to-reorder — the pure core, extracted so it is unit-testable without d3/DOM.
//
// Given the rendered layout `nodes`, the dragged person node, and the pixel-x it was dropped
// at, compute the NEW left-to-right order (Array<uid>) for the dragged node's GENERATION ROW
// only. The caller merges this into the cross-generation establishedOrder Map by index
// (rank = position in the returned array), leaving every other generation's ranks untouched.
//
// The drag is interpreted entirely in PIXEL space — what the user actually sees and drops
// against — NOT in virtualX. virtualX and pixel-x normally agree within an established row,
// but a customPositions pin on a peer (buildRenderModel overrides x, layout.js) can break that
// monotonicity; mixing the two spaces gave false no-ops and incoherent orders. So we order the
// OTHER peers by their rendered x, insert the dragged node at the pixel slot it was dropped in,
// and detect a no-op by comparing that slot to where the node STARTED (its pre-drag x).
//
// Contract (must stay aligned with captureEstablishedOrder in layout.js so a drag-derived order
// and a capture-derived order feed _applyLockedOrder identically):
//  - The order is the WHOLE generation row (grouped by Math.round(virtualY)), NOT a sibship —
//    _applyLockedOrder re-ranks the entire row, so a sibling-only order would violate the
//    contract. The user perceives a sibling swap; the data model reorders the row.
//  - Persons with uid == null are excluded (they cannot be ranked — see layout.js:485).
//
// `originalX` is the dragged node's x BEFORE the drag mutated it (the caller captures it at drag
// start; `dragged.x` by drag-end is the drop point, so it can't serve as the origin). Defaults
// to `dragged.x` for callers/tests where the node wasn't mutated.
//
// Returns Array<uid> (contiguous 0..n ranks when indexed), or null when there is nothing to do:
// fewer than 2 rankable peers, or the node ends in the slot it started in (a no-op drag the
// caller must snap back without persisting — otherwise every micro-drag fires a save).
export function computeReorderForGeneration(nodes, dragged, dropX, originalX) {
  if (!dragged || dragged.uid == null) return null;
  const startX = originalX ?? dragged.x ?? 0;

  const gen = Math.round(dragged.virtualY ?? 0);
  const peers = (nodes || []).filter(
    (n) =>
      n &&
      n.kind === config.NODE_TYPE.NODE &&
      n.uid != null &&
      Math.round(n.virtualY ?? 0) === gen
  );
  if (peers.length < 2) return null;

  // The other peers, ordered by what's on screen (rendered x). dragged is excluded — its x is
  // the live drop point, not a stable position. Tie-break by virtualX then uid for determinism.
  const others = peers
    .filter((n) => n.uid !== dragged.uid)
    .sort(
      (a, b) =>
        (a.x ?? 0) - (b.x ?? 0) ||
        (a.virtualX ?? 0) - (b.virtualX ?? 0) ||
        String(a.uid).localeCompare(String(b.uid))
    );

  // Both slots counted in the SAME (pixel) space: where the node is dropped vs where it started.
  let insertIdx = 0;
  for (const n of others) if ((n.x ?? 0) < dropX) insertIdx++;
  let startIdx = 0;
  for (const n of others) if ((n.x ?? 0) < startX) startIdx++;

  if (insertIdx === startIdx) return null; // ends where it began — snap back, do not persist

  const result = [
    ...others.slice(0, insertIdx),
    dragged,
    ...others.slice(insertIdx)
  ];
  return result.map((n) => n.uid);
}

// Merge a single generation's new uid order into the full cross-generation order Map,
// re-ranking ONLY that generation (0..n) and preserving every other generation's ranks.
// Returns a new Map (never mutates `prev`).
export function mergeGenerationOrder(prev, uidsInOrder) {
  const next = new Map(prev || []);
  uidsInOrder.forEach((uid, i) => next.set(uid, i));
  return next;
}
